interface LogoPerformanceMetrics {
  startTime: number;
  endTime: number;
  loadTime: number;
  size: number;
  url: string;
}

class LogoPerformanceMonitor {
  private metrics: Map<string, LogoPerformanceMetrics> = new Map();
  private startTime: number = 0;

  /**
   * Start monitoring logo loading performance
   */
  startMonitoring(): void {
    this.startTime = performance.now();
  }

  /**
   * Record logo load start
   */
  recordLogoStart(url: string): void {
    this.metrics.set(url, {
      startTime: performance.now(),
      endTime: 0,
      loadTime: 0,
      size: 0,
      url
    });
  }

  /**
   * Record logo load completion
   */
  recordLogoComplete(url: string, size?: number): void {
    const metric = this.metrics.get(url);
    if (metric) {
      metric.endTime = performance.now();
      metric.loadTime = metric.endTime - metric.startTime;
      if (size) metric.size = size;
      
      // Log performance data
      const loadTimeMs = metric.loadTime.toFixed(2);
      const loadTimeMicros = (metric.loadTime * 1000).toFixed(2);
      
      if (metric.loadTime < 100) {
        // Fast load (< 100ms)
      } else if (metric.loadTime < 500) {
        // Good load (100-500ms)
      } else if (metric.loadTime < 1000) {
        // Slow load (500ms-1s) - warn but not critical
        console.warn(`⚠️ Logo loaded slowly in ${loadTimeMs}ms - ${url}`);
      } else {
        // Very slow load (> 1s) - critical warning
        console.error(`🚨 Logo loaded very slowly in ${loadTimeMs}ms - ${url}`);
      }
    }
  }

  /**
   * Get overall performance summary
   */
  getPerformanceSummary(): {
    totalLogos: number;
    averageLoadTime: number;
    fastestLoad: number;
    slowestLoad: number;
    totalLoadTime: number;
  } {
    const metrics = Array.from(this.metrics.values());
    const totalLogos = metrics.length;
    
    if (totalLogos === 0) {
      return {
        totalLogos: 0,
        averageLoadTime: 0,
        fastestLoad: 0,
        slowestLoad: 0,
        totalLoadTime: 0
      };
    }

    const totalLoadTime = metrics.reduce((sum, m) => sum + m.loadTime, 0);
    const averageLoadTime = totalLoadTime / totalLogos;
    const fastestLoad = Math.min(...metrics.map(m => m.loadTime));
    const slowestLoad = Math.max(...metrics.map(m => m.loadTime));

    return {
      totalLogos,
      averageLoadTime,
      fastestLoad,
      slowestLoad,
      totalLoadTime
    };
  }

  /**
   * Log performance summary
   */
  logPerformanceSummary(): void {
    const summary = this.getPerformanceSummary();
    
    if (summary.totalLogos === 0) return;

    const totalTime = (performance.now() - this.startTime).toFixed(2);
    const avgTime = (summary.averageLoadTime * 1000).toFixed(2);
    const fastest = (summary.fastestLoad * 1000).toFixed(2);
    const slowest = (summary.slowestLoad * 1000).toFixed(2);

    // Performance summary

    // Check if we met the <200μs target
    // Performance target check
  }

  /**
   * Clear all metrics
   */
  clearMetrics(): void {
    this.metrics.clear();
  }
}

// Export singleton instance
export const logoPerformanceMonitor = new LogoPerformanceMonitor();
export default LogoPerformanceMonitor;
